import { createRoot, type Root } from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import { openSession, watchNativeSignOut } from "@/api/session";
import { AppProviders } from "@/app/providers";
import { router } from "@/app/router";
import { readBootstrap } from "@/bridge/bootstrap";
import { call, installBridge } from "@/bridge/bridge";

import "@/theme/globals.css";

function removeSkeleton(): void {
  document.getElementById("app-skeleton")?.remove();
}

function renderUnavailable(root: Root): void {
  removeSkeleton();
  root.render(
    <main className="safe-top flex min-h-dvh flex-col items-center justify-center px-4 py-4">
      <p className="text-16 text-center">
        ไม่สามารถเปิดหน้านี้ได้ กรุณาลองใหม่อีกครั้ง
      </p>
      <button
        type="button"
        className="text-16 mt-4 h-[34px] rounded-pill border border-primary px-6 font-bold text-primary"
        onClick={() => {
          void call("nav.back").catch(() => {
            // No shell (plain browser): nothing to close.
          });
        }}
      >
        กลับ
      </button>
    </main>,
  );
}

async function mount(): Promise<void> {
  const container = document.getElementById("root");
  if (container === null) {
    throw new Error('index.html is missing <div id="root">.');
  }

  installBridge();
  watchNativeSignOut();

  const bootstrap = readBootstrap();
  const root = createRoot(container);

  if (!bootstrap.ok) {
    console.error("[bootstrap]", bootstrap.reason);
    renderUnavailable(root);
    return;
  }

  if (!(await openSession(bootstrap.data.exchangeCode))) {
    console.error("[session] this page could not open a session");
    renderUnavailable(root);
    return;
  }

  removeSkeleton();
  root.render(
    <AppProviders
      bootstrap={{ data: bootstrap.data, source: bootstrap.source }}
    >
      <RouterProvider router={router} />
    </AppProviders>,
  );
}

void mount();
